import { useGetVideo } from '@vimeo/gctl-api/hooks/useVideo';
import { VideoFields } from '@vimeo/gctl-api/models/components/schemas/video/Video';
import { useGetAlbumVideoData } from '@vimeo/use-get-album-video';
import { useGetUnlockedVideo } from '@vimeo/use-unlocked-video';

import { USER_PERMISSION_FIELDS } from '../utils/constants';
import {
  extractClipData,
  getReviewPasswordHashFromCookie,
  getVideoIdFromClipRequestId,
  mapToClipFields,
} from '../utils/helper';
import { useAuthenticatedViewer, useViewer } from './use-viewer';

export type UserPermission = {
  isOwner: boolean;
  canEdit: boolean;
  canDelete: boolean;
};

export function useUserPermissions(
  videoId: string,
  showcaseId?: number,
  reviewId?: string,
): UserPermission {
  const { data: videoData } = useVideoDataForPermissions(videoId, showcaseId, reviewId);

  const loggedInViewer = useAuthenticatedViewer();
  const viewerUri = loggedInViewer?.user?.uri;
  const ownerUri = videoData?.user?.uri;
  const videoInteractions = videoData?.metadata.interactions;
  const isOwner = !!viewerUri && ownerUri === viewerUri;
  const canEdit = !!videoInteractions?.edit;
  const canDelete = !!videoInteractions?.delete;
  return {
    isOwner,
    canEdit,
    canDelete,
  };
}

/**
 * Custom hook to fetch video data based on the context (regular video, review link, or showcase)
 */
const useVideoDataForPermissions = (videoId: string, showcaseId?: number, reviewId?: string) => {
  const viewer = useViewer();

  const reviewVideoData = useGetVideo(
    // @ts-expect-error videoId expects a number, but doesn't account for clipHash strings
    () => {
      if (!viewer || !videoId || !reviewId || showcaseId) return null;

      const password = getReviewPasswordHashFromCookie(reviewId);
      return {
        where: { videoId },
        select: USER_PERMISSION_FIELDS,
        query: { reviewId, password },
      };
    },
    {
      revalidateOnFocus: false,
    },
  );

  const albumVideoData = useGetAlbumVideoData(
    showcaseId ? showcaseId : null,
    Number(getVideoIdFromClipRequestId(videoId)),
    mapToClipFields(USER_PERMISSION_FIELDS as VideoFields[]),
  );

  const videoData = useGetUnlockedVideo(
    // @ts-expect-error videoId expects a number, but doesn't account for clipHash strings
    () => {
      if (!viewer || !videoId || showcaseId || reviewId) return null;

      return {
        where: { videoId },
        select: USER_PERMISSION_FIELDS,
      };
    },
    {
      revalidateOnFocus: false,
    },
  );

  if (reviewId) {
    return reviewVideoData;
  }

  if (showcaseId) {
    return {
      data: extractClipData(albumVideoData.data as Record<string, unknown>),
    };
  }

  return videoData;
};
